import React, { useCallback, useMemo } from 'react';

import { LinearProgress } from '@mui/material';
import { IconAlertTriangle } from '@tabler/icons-react';

import { Tooltip } from '@/components/alerts/Tooltip';
import { useNavigate } from '@/hooks/useNavigate';
import { theme } from '@/theme';

import {
    StyledChip,
    StyledLabelWrapper,
    StyledListItemButton,
    StyledListItemContent,
    StyledListItemIcon,
    StyledListItemText,
    StyledLoaderStack,
} from './SidebarMenuListItem.styles';
import { SidebarMenuItem } from './SidebarMenuListItem.types';

export interface SidebarListIconsItemElementProps extends SidebarMenuItem {
    isStatisticsLoading: boolean;
    className?: string;
    selected: boolean;
}

export const SidebarMenuListItem = ({
    icon,
    text,
    pathname,
    statistics,
    warnings,
    isStatisticsLoading,
    className,
    selected,
}: SidebarListIconsItemElementProps) => {
    const { browserNavigate } = useNavigate();

    const additionalInfo = useMemo(
        () =>
            statistics === undefined && !warnings ? null : (
                <StyledLabelWrapper>
                    {warnings ? (
                        <Tooltip
                            title={warnings}
                            disableInteractive
                            placement="top"
                            tooltipBackground={theme.palette.statistics.red}
                        >
                            <IconAlertTriangle size={16} color={theme.palette.statistics.red} />
                        </Tooltip>
                    ) : null}
                    {statistics === undefined ? null : isStatisticsLoading ? (
                        <StyledLoaderStack>
                            <LinearProgress color="primary" />
                        </StyledLoaderStack>
                    ) : (
                        <StyledChip size="small" label={statistics} />
                    )}
                </StyledLabelWrapper>
            ),
        [isStatisticsLoading, statistics, warnings],
    );

    const handleClick = useCallback(
        (event: React.MouseEvent) => browserNavigate({ event, to: pathname }),
        [browserNavigate, pathname],
    );

    return (
        <StyledListItemButton
            component="a"
            href={pathname}
            selected={selected}
            className={className}
            onClick={handleClick}
        >
            {icon ? <StyledListItemIcon className="list-item-icon">{icon}</StyledListItemIcon> : null}
            <StyledListItemContent>
                <StyledListItemText primary={text} />
                {additionalInfo}
            </StyledListItemContent>
        </StyledListItemButton>
    );
};
